import { useRef, useImperativeHandle, forwardRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  checkRichTextLength,
  type RichTextLimitVariant,
} from "@/utils/richText";

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  maxLength?: number;
  limitVariant?: RichTextLimitVariant;
}

export interface RichTextEditorRef {
  insertText: (text: string) => void;
}

const modules = {
  toolbar: [
    ["bold", "italic"],
    [{ list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const formats = ["bold", "italic", "list", "bullet", "align", "link"];

export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      content,
      onChange,
      placeholder = "Write your content here...",
      maxLength,
      limitVariant = "default",
    },
    ref
  ) => {
    const quillRef = useRef<ReactQuill>(null);

    useImperativeHandle(ref, () => ({
      insertText: (text: string) => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection();
          const index = range?.index || quill.getLength();
          quill.insertText(index, text);
          const newIndex = index + text.length;
          quill.setSelection(newIndex, 0);
        }
      },
    }));

    const { plainLength, htmlLength, isOver } =
      maxLength !== undefined
        ? checkRichTextLength(content, maxLength)
        : { plainLength: 0, htmlLength: 0, isOver: false };

    return (
      <div className="border border-border rounded-lg overflow-hidden">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={content}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          className="bg-transparent"
        />
        {maxLength !== undefined && (
          <div className="flex items-center justify-between gap-3 px-3 py-1.5 bg-transparent border-t border-border">
            <span className="text-xs text-red-500 truncate">
              {isOver
                ? limitVariant === "email"
                  ? `As an email template: ${htmlLength} / ${maxLength}`
                  : `With formatting: ${htmlLength} / ${maxLength}`
                : ""}
            </span>
            <span
              className={`text-xs tabular-nums shrink-0 ${
                isOver ? "text-red-500" : "text-muted-foreground"
              }`}
            >
              {plainLength} / {maxLength}
            </span>
          </div>
        )}
        <style>{`
        .ql-toolbar {
          border: none !important;
          border-bottom: 1px solid hsl(var(--border)) !important;
          background-color: hsl(var(--muted));
          padding: 8px !important;
        }
        .ql-toolbar.ql-snow .ql-stroke {
          stroke: hsl(var(--foreground));
        }
        .ql-toolbar.ql-snow .ql-fill {
          fill: hsl(var(--foreground));
        }
        .ql-toolbar.ql-snow .ql-picker {
          color: hsl(var(--foreground));
        }
        .ql-toolbar.ql-snow .ql-picker-options {
          background-color: hsl(var(--card));
          border-color: hsl(var(--border)) !important;
        }
        .ql-container {
          border: none !important;
          min-height: 200px;
          background-color: transparent;
          color: hsl(var(--foreground));
        }
        .ql-editor {
          min-height: 200px;
          font-size: 14px;
          padding: 16px;
        }
        .ql-editor.ql-blank::before {
          font-style: normal;
          color: hsl(var(--muted-foreground));
        }
        .ql-editor a {
          color: rgb(var(--brand-600));
          text-decoration: underline;
        }
      `}</style>
      </div>
    );
  }
);
